import React, { useEffect, useState, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  fetchAuditLogs,
  fetchAuditLogById,
  exportAuditLogs,
} from "../services/auditService";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import moment from "moment";
import { getModulePermissions } from "../../utils/permissionUtils";
import { useNavigate } from "react-router-dom";

const ListAuditHistoryPageNew = () => {
  const navigate = useNavigate();
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalLogs, setTotalLogs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [viewLog, setViewLog] = useState(null);
  const [exportType, setExportType] = useState(""); // CSV or XLSX
  const [exportLoading, setExportLoading] = useState(false);
  const [search, setSearch] = useState("");
  // Sorting states
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const { shouldCheckPermissions, canEdit, canAdd, canView, canDelete } =
    getModulePermissions("Audit History");

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
        endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
        search: search || "",
        sortBy: sortColumn,
        sortOrder,
      };

      const response = await fetchAuditLogs(params);

      // API returns data.data as array of logs, data.pagination.total for total count
      setLogs(response.data.data || []);
      setTotalLogs(response.data.pagination?.total || 0);
      setCurrentPage(response.data.pagination?.page || 1);
      setItemsPerPage(response.data.pagination?.limit || itemsPerPage);
    } catch (err) {
      setErrorMessage(err.message || "Failed to fetch audit logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage, startDate, endDate, sortOrder, sortColumn]);

  const handleExport = async () => {
    try {
      const params = {
        format: exportType, // 'csv' or 'xlsx'
        startDate: startDate
          ? moment(startDate).format("YYYY-MM-DD")
          : undefined,
        endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : undefined,
      };
      const response = await exportAuditLogs(params);

      // Extract filename from response headers (if backend sends it)
      const contentDisposition = response.headers["content-disposition"];
      let filename = "audit_logs.xlsx";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?($|;)/);
        if (match) filename = match[1];
      } else {
        // fallback based on selected exportType
        filename = exportType === "csv" ? "audit_logs.csv" : "audit_logs.xlsx";
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  const totalPages = Math.ceil(totalLogs / itemsPerPage);

  const getPaginationRange = () => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const formatLabel = (str) => {
    if (!str) return "-";
    return str
      .split("_") // split by underscore
      .map((word) => word[0].toUpperCase() + word.slice(1)) // capitalize first letter
      .join(" "); // join with space
  };

  const isSelected = (id) => selectedLogs.includes(id);

  const handleSelectAll = (e) => {
    setSelectedLogs(e.target.checked ? logs.map((log) => log._id) : []);
  };

  const handleSelectLog = (id) => {
    setSelectedLogs((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSort = (column) => {
    setSortColumn(column);
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };
  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value, 10));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10 items per page</option>
            <option value={20}>20 items per page</option>
            <option value={50}>50 items per page</option>
            <option value={100}>100 items per page</option>
          </select>

          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              placeholder="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
                fetchData();
              }}
              style={{
                paddingRight: search ? "5rem" : "2.5rem",
                borderRadius: "7px",
              }}
            />

            {/* Search icon */}
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: search ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />

            {/* Clear button */}
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setCurrentPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>
          {/* Start Date */}
          <div style={{ position: "relative", maxWidth: "200px", flex: 1 }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                setCurrentPage(1);
                fetchData();
              }}
              className="form-control"
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
            />
            {startDate && (
              <button
                type="button"
                onClick={() => {
                  setStartDate(null);
                  setCurrentPage(1);
                  fetchData();
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "35%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "1.25rem",
                  cursor: "pointer",
                  zIndex: 10,
                  pointerEvents: "all",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </div>

          {/* End Date */}
          <div style={{ position: "relative", maxWidth: "200px", flex: 1 }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                setEndDate(date);
                setCurrentPage(1);
                fetchData();
              }}
              className="form-control"
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
            />
            {endDate && (
              <button
                type="button"
                onClick={() => {
                  setEndDate(null);
                  setCurrentPage(1);
                  fetchData();
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "35%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "1.25rem",
                  cursor: "pointer",
                  zIndex: 2,
                  pointerEvents: "all",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </div>

          {/* Export Section */}
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={exportType}
              onChange={(e) => setExportType(e.target.value)}
            >
              <option disabled value="">
                Export as
              </option>
              <option value="csv">CSV</option>
              <option value="xlsx">XLSX</option>
            </select>

            <button
              className="btn btn-primary"
              style={{ borderColor: "#FA69B9" }}
              onClick={handleExport}
              disabled={!exportType || exportLoading}
            >
              {exportLoading ? "Exporting..." : "Export"}
            </button>
          </div>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th scope="col">
                  <div className="d-flex align-items-center gap-10">
                    <div className="form-check style-check d-flex align-items-center">
                      <input
                        type="checkbox"
                        name="checkbox"
                        className="form-check-input radius-4 border input-form-dark"
                        checked={
                          selectedLogs.length === logs.length && logs.length > 0
                        }
                        onChange={handleSelectAll}
                      />
                    </div>
                  </div>
                </th>


                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-10">
                    Date
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("adminId.name")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-10">
                    Admin Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "adminId.name" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "adminId.name" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("adminId.email")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-10">
                    Admin Email
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "adminId.email" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "adminId.email" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Admin Role</th>
                <th>Action Performed</th>
                <th
                  onClick={() => handleSort("userId.email")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-10">
                    Target
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "userId.email" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "userId.email" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : logs.length > 0 ? (
                logs.map((log) => (
                  <tr key={log._id}>
                    <td>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={isSelected(log._id)}
                          onChange={() => handleSelectLog(log._id)}
                        />
                      </div>
                    </td>
                    <td>{moment(log.createdAt).format("DD-MM-YYYY")}</td>
                    <td>{log?.adminId?.name}</td>
                    <td>{log?.adminId?.email}</td>

                    <td>{formatLabel(log?.adminId?.role)}</td>
                    <td>{formatLabel(log?.action)}</td>
                    <td>
                      {log?.userId?.email}    {log?.matchedUserId && ` → ${log.matchedUserId.email}`}
                    </td>
                    <td>
                      {(!shouldCheckPermissions || canView) && (
                        <button
                          title="View"
                          onClick={() =>
                            navigate(`/audit-history/view/${log._id}`)
                          }
                          className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="majesticons:eye-line" className="icon text-xl" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Audit Logs Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalLogs > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalLogs)} of {totalLogs}{" "}
              entries
            </span>

            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
              {/* Previous Button */}
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() =>
                    currentPage > 1 && setCurrentPage(currentPage - 1)
                  }
                >
                  <Icon icon="ep:d-arrow-left" />
                </button>
              </li>

              {/* Page Numbers */}
              {getPaginationRange().map((p) => (
                <li key={p} className="page-item">
                  <button
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === p
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                      }`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                </li>
              ))}

              {/* Next Button */}
              <li
                className={`page-item ${currentPage === totalPages ? "disabled" : ""
                  }`}
              >
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() =>
                    currentPage < totalPages && setCurrentPage(currentPage + 1)
                  }
                >
                  <Icon icon="ep:d-arrow-right" />
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListAuditHistoryPageNew;
